/* eslint-disable @typescript-eslint/no-unused-vars */
import { useRef, useState } from 'react'
import * as Yup from 'yup'
import { Link, useNavigate } from 'react-router-dom'
import { Field, FormikProvider, useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { register } from '../../../redux/features/auth/authActions'
import { CITY_REQUIRED, COMPANY_NAME_REQUIRED, COUNTRY_REQUIRED, EMAIL_REQUIRED, INVALID_COUNTRY, INVALID_EMAIL_MESSAGE, INVALID_PEC_NUMBER, INVALID_PHONE_NUMBER, INVALID_PROVINCE_CODE, INVALID_SDI_NUMBER, INVALID_VAT_NUMBER, PASSWORD_LENGTH_MESSAGE, PASSWORD_REQUIRED, PEC_REQUIRED, PHONE_NUMBER_REQUIRED, PROVINCE_CODE_REQUIRED, SDI_REQUIRED, SHOP_ADDRESS_REQUIRED, VAT_REQUIRED, ZIP_CODE_REQUIRED } from '../../../utlis/messages'
import FieldInputText from '../../common/FieldInputText'
import SubmitButton from '../../common/SubmitButton'
import axios from 'axios'
import { Autocomplete } from '@react-google-maps/api'
import { ISO_COUNTRIES } from '../../../utlis/isoCountryCodes'
import { COUNTRY_NAME_TO_ISO } from '../../../utlis/countryNameToIso'
import { ITALIAN_PROVINCE_CODES } from '../../../utlis/italianProvinces'

const VAT_REGEX = /^$|^([Ii][Tt])?(\d{11})$|^(?![iI][tT])([a-zA-Z]{2,3})(\d+)$/;
const PEC_REGEX = /^$|^([^ @]+)@([^ @]+)\.([a-zA-Z]{2,})$/;
const SDI_REGEX = /^$|^([\w\d]{7})$/;

const validationSchema = Yup.object({
  email: Yup.string().email(INVALID_EMAIL_MESSAGE).required(EMAIL_REQUIRED),
  password: Yup.string().min(8, PASSWORD_LENGTH_MESSAGE).required(PASSWORD_REQUIRED),
  shopAddress: Yup.string().required(SHOP_ADDRESS_REQUIRED),
  companyName: Yup.string().required(COMPANY_NAME_REQUIRED),
  vat: Yup.string().required(VAT_REQUIRED).matches(VAT_REGEX, INVALID_VAT_NUMBER),
  street: Yup.string(),
  houseNumber: Yup.string(),
  zipCode: Yup.string().required(ZIP_CODE_REQUIRED),
  country: Yup.string().required(COUNTRY_REQUIRED).oneOf(ISO_COUNTRIES, INVALID_COUNTRY),
  city: Yup.string().required(CITY_REQUIRED),
  provinceCode: Yup.string().required(PROVINCE_CODE_REQUIRED).matches(/^[A-Z]{2}$/, INVALID_PROVINCE_CODE),
  phone: Yup.string().required(PHONE_NUMBER_REQUIRED).matches(/^\+?\d{7,15}$/, INVALID_PHONE_NUMBER),
  pec: Yup.string().matches(PEC_REGEX, INVALID_PEC_NUMBER),
  sdi: Yup.string().required(SDI_REQUIRED).matches(SDI_REGEX, INVALID_SDI_NUMBER),
});

const initialValues = {
  email: '',
  password: '',
  shopAddress: '',
  companyName: '',
  vat: '',
  street: '',
  houseNumber: '',
  zipCode: '',
  country: '',
  city: '',
  provinceCode: '',
  phone: '',
  pec: '',
  sdi: '',
}

export function Registration() {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const authState = useSelector((state: any) => state.auth);
  const autocompleteRef = useRef<any>(null);

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    validate: async (values) => {
      if (!values.shopAddress) {
        return { shopAddress: SHOP_ADDRESS_REQUIRED };
      }
    },
    onSubmit: async (values) => {
      try {
        await dispatch(register({ values, navigate }));
      } catch (error) {
        console.error("Registration failed:", error);
      }
    },
  });

  const handlePlaceChanged = () => {
    const autocomplete = autocompleteRef.current;
    if (!autocomplete) return;

    const place = autocomplete.getPlace();
    if (!place.geometry || !place.address_components) return;

    const components = place.address_components;
    console.log('components ====== ', components);

    const getComponent = (type: string, short = false) => {
      const comp = components.find((c: any) => c.types.includes(type));
      if (!comp) return '';
      return short ? comp.short_name : comp.long_name;
    };

    // Extracting desired fields
    const streetNumber = getComponent('street_number');
    const route = getComponent('route');
    const sublocality = getComponent('sublocality') || getComponent('sublocality_level_1');
    const neighborhood = getComponent('neighborhood');
    const premise = getComponent('premise');
    const establishment = getComponent('establishment');

    // Optional building name or business name
    const name = place.name;

    // Use the most specific, human-readable parts
    const rawAddressParts = [
      name,
      premise,
      establishment,
      neighborhood,
      sublocality,
    ];

    const uniqueParts = Array.from(new Set(rawAddressParts.filter(Boolean))); // removes duplicates and falsy values
    const simplifiedAddress = uniqueParts.join(', ');

    // Full address info
    const zipCode = getComponent('postal_code');
    const city = getComponent('locality') || getComponent('administrative_area_level_2');
    const provinceShort = getComponent('administrative_area_level_2', true) || getComponent('administrative_area_level_1', true);
    let countryCode = getComponent('country', true);
    const countryName = getComponent('country', false);

    if (!ISO_COUNTRIES.includes(countryCode)) {
      countryCode = COUNTRY_NAME_TO_ISO[countryName] || '';
    }

    // Update Formik values
    formik.setFieldValue('shopAddress', simplifiedAddress);
    if (zipCode) formik.setFieldValue('zipCode', zipCode);
    if (city) formik.setFieldValue('city', city);
    if (countryCode) formik.setFieldValue('country', countryCode.toUpperCase());
    if (route) formik.setFieldValue('street', route); // Via
    if (streetNumber) formik.setFieldValue('houseNumber', streetNumber); // Numero Civico

    const upperProvince = provinceShort?.toUpperCase();
    if (countryCode?.toUpperCase() === 'IT' && upperProvince && /^[A-Z]{2}$/.test(upperProvince)) {
      formik.setFieldValue('provinceCode', upperProvince);
    } else {
      formik.setFieldValue('provinceCode', '');
    }
  };


  return (
    <FormikProvider value={formik}>
      <form
        className='form w-100 authWrapper'
        onSubmit={formik.handleSubmit}
        noValidate
        id='kt_register_form'
      >
        <div className='text-center mb-5'>
          <h1 className='text-dark fw-bolder mb-3'>Iscrizione</h1>
        </div>

        <div className='fv-row mb-3'>
          <Field name="email" label="E-mail" type="email" required component={FieldInputText} placeholder="E-mail" />
        </div>

        <div className='fv-row mb-3'>
          <Field name="password" label="Password" type="password" required component={FieldInputText} placeholder="Password" />
        </div>

        <div className='fv-row mb-3'>
          <label className='form-label required'><strong>Indirizzo del negozio</strong></label>
          <div className="form-text text-muted mb-1">
            *selezionare da menu suggerimenti a tendina per assicurarsi di completare correttamente il campo
          </div>
          <Autocomplete onLoad={(ref) => (autocompleteRef.current = ref)} onPlaceChanged={handlePlaceChanged}>
            <input
              type="text"
              name="shopAddress"
              className="commonInput form-control"
              placeholder="Indirizzo del negozio"
              value={formik.values.shopAddress}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Autocomplete>
          {formik.touched.shopAddress && formik.errors.shopAddress && (
            <div className="text-danger">{formik.errors.shopAddress}</div>
          )}
        </div>

        <div className='fv-row mb-3'>
          <Field name="companyName" label="Ragione sociale" type="text" required component={FieldInputText} placeholder="Ragione sociale" hint="*inserire ragione sociale della propria attività" />
        </div>

        <div className="d-flex gap-3 mb-0">
          <div className="w-50">
            <Field name="vat" label="Partita Iva" type="text" required component={FieldInputText} placeholder="VAT" hint="*inserisci partita iva senza spazi es. 00154130207" />
          </div>
          <div className="w-50">
            <Field name="street" label="Via" type="text" component={FieldInputText} placeholder="Via" />
          </div>
        </div>

        <div className="d-flex gap-3 mb-0">
          <div className="w-50">
            <Field name="houseNumber" label="Numero Civico" type="text" component={FieldInputText} placeholder="Numero Civico" />
          </div>
          <div className="w-50">
            <Field name="zipCode" label="CAP" type="string" required component={FieldInputText} placeholder="CAP" />
          </div>
        </div>

        <div className="d-flex gap-3 mb-0">
          <div className="w-50">
            <Field name="country" label="Codice Stato" type="string" required component={FieldInputText} placeholder="Codice Stato" hint="inserisci il codice es. italia inserire IT" />
          </div>
          <div className="w-50">
            <Field name="city" label="Città" type="string" required component={FieldInputText} placeholder="Città" />
          </div>
        </div>

        <div className="d-flex gap-3 mb-3">
          <div className="w-50">
            <Field name="provinceCode" label="Codice Provincia" type="string" required component={FieldInputText} placeholder="Codice Provincia" hint="*inserisci il codice provincia es. Mantova inserisci MN" />
          </div>
          <div className="w-50">
            {/* <Field name="phone" label="Telefono" type="text" required component={FieldInputText} placeholder="Telefono" /> */}
            <Field
              name="phone"
              label="Telefono"
              type="text"                 // ✅ Use text to control formatting
              inputMode="numeric"         // ✅ Shows number keypad on mobile
              pattern="[0-9+]*"           // ✅ Allows only digits and optional '+'
              required
              component={FieldInputText}
              placeholder="Telefono"
            />
          </div>
        </div>

        <div className="d-flex gap-3 mb-0">
          <div className="w-50">
            <Field name="pec" label="PEC" type="string" component={FieldInputText} placeholder="PEC" hint="*inserisci l'indirizzo pec per la fatturazione elettronica" />
          </div>
          <div className="w-50">
            <Field name="sdi" label="SDI" type="string" required component={FieldInputText} placeholder="SDI" hint="*inserisci il tuo codice sdi per la fatturazione elettronica" />
          </div>
        </div>

        <div className='d-grid mb-3'>
          <SubmitButton title="Iscrizione" type="submit" loading={authState.loading} />
        </div>

        {/* Add Sign-in Link */}
        <div className='text-center'>
          <span className='text-gray-600 fw-semibold'>Hai già un account?{" "}
            <Link to='/auth/login' className='link-primary'>
              Accedi
            </Link>
          </span>
        </div>
      </form>
    </FormikProvider>
  )
}
